import React, { useState, useEffect } from "react";
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Text,
  Alert,
} from "@mantine/core";
import { IconAlertCircle, IconTrash } from "@tabler/icons-react";

const EditRecruitingSourceModal = ({
  opened,
  onClose,
  source,
  onSave,
  onDelete,
}) => {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  useEffect(() => {
    if (opened && source) {
      setName(source.source_name || "");
      setError("");
    } else if (opened) {
      // For creating new recruiting sources
      setName("");
      setError("");
    }
  }, [opened, source]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Source name is required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const updatedSource = {
        ...source,
        name: name.trim(),
      };

      await onSave(updatedSource);
    } catch (err) {
      setError(err.message || "Failed to save recruiting source");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
    setError(""); // Clear any existing errors
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      await onDelete(source.id);
      handleClose();
    } catch (err) {
      setError(err.message || "Failed to delete recruiting source");
      setShowDeleteConfirmation(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
  };

  const handleClose = () => {
    setName("");
    setError("");
    setShowDeleteConfirmation(false);
    onClose();
  };

  const isNew = !source || !source.id;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isNew ? "Add Recruiting Source" : "Edit Recruiting Source"}
      size="md"
    >
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <TextInput
          label="Source Name"
          placeholder="Enter recruiting source name"
          value={name}
          onChange={e => setName(e.target.value)}
          required
          error={!name.trim() && error ? "Name is required" : null}
        />

        {showDeleteConfirmation && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            <Stack gap="sm">
              <Text size="sm">
                Are you sure you want to delete "{source?.source_name}"? This
                action cannot be undone.
              </Text>
              <Group gap="sm">
                <Button
                  size="xs"
                  color="red"
                  onClick={handleDeleteConfirm}
                  loading={loading}
                >
                  Delete
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={handleDeleteCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        <Group justify="space-between" gap="sm">
          {!isNew && onDelete && !showDeleteConfirmation && (
            <Button
              variant="outline"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={handleDeleteClick}
              disabled={loading}
            >
              Delete
            </Button>
          )}

          <Group justify="flex-end" gap="sm" style={{ marginLeft: "auto" }}>
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              loading={loading}
              disabled={!name.trim() || showDeleteConfirmation}
            >
              {isNew ? "Add Source" : "Save Changes"}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

export default EditRecruitingSourceModal;
